/**
 * VerpflegungWidget — Dashboard-Widget fuer Mitarbeiter-Mahlzeiten-Dokumentation
 * Grosser Button: "Ich habe heute ein Mittagessen entnommen"
 * Nachtrags-Funktion fuer vergessene Tage (letzte 5 Arbeitstage)
 * Mobile-optimiert
 */

import { useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useVerzehr } from '@/hooks/useVerpflegung';
import { localIsoDate } from '@/lib/date-utils';
import { SACHBEZUGSWERT_PRO_TAG } from '@/lib/database.types';
import type { VerpflegungFirma } from '@/lib/database.types';
import { UtensilsCrossed, Check, Plus, ChevronDown, ChevronUp, Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const KOOP_RED = '#C41E3A';

function getLetzteArbeitstage(count: number): string[] {
  const tage: string[] = [];
  const d = new Date();
  d.setDate(d.getDate() - 1); // start from yesterday
  while (tage.length < count) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) {
      tage.push(localIsoDate(d));
    }
    d.setDate(d.getDate() - 1);
  }
  return tage;
}

export default function VerpflegungWidget() {
  const { profile } = useAuth();
  const [firma, setFirma] = useState<VerpflegungFirma>('Koop');
  const { verzehrListe, loading, heuteErfasst, erfasseVerzehr } = useVerzehr(profile?.id, firma);
  const [showNachtrag, setShowNachtrag] = useState(false);
  const [saving, setSaving] = useState(false);

  const letzteArbeitstage = useMemo(() => getLetzteArbeitstage(5), []);
  const erfassteTage = useMemo(
    () => new Set(verzehrListe.map(v => v.datum)),
    [verzehrListe]
  );

  const heute = localIsoDate(new Date());

  const handleHeuteErfassen = async () => {
    setSaving(true);
    const ok = await erfasseVerzehr();
    setSaving(false);
    if (ok) {
      toast.success('Mahlzeit dokumentiert');
    } else {
      toast.error('Bereits dokumentiert oder Fehler aufgetreten');
    }
  };

  const handleNachtrag = async (datum: string) => {
    setSaving(true);
    const ok = await erfasseVerzehr(datum);
    setSaving(false);
    if (ok) {
      toast.success(`Nachtrag fuer ${new Date(datum).toLocaleDateString('de-DE')} gespeichert`);
    } else {
      toast.error('Bereits dokumentiert oder Fehler aufgetreten');
    }
  };

  // Anzahl Mahlzeiten im aktuellen Monat
  const mahlzeitenMonat = useMemo(() => {
    const prefix = heute.slice(0, 7);
    return verzehrListe.filter(v => v.datum.startsWith(prefix)).length;
  }, [verzehrListe, heute]);

  return (
    <div className="space-y-3">
      {/* Firma-Auswahl */}
      <div className="flex items-center gap-2">
        {(['LCS', 'Koop'] as VerpflegungFirma[]).map(f => (
          <button
            key={f}
            onClick={() => setFirma(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
              firma === f
                ? 'text-white shadow-sm'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
            style={firma === f ? { backgroundColor: KOOP_RED } : undefined}
          >
            {f === 'LCS' ? 'LCS GmbH' : 'Gutachter Koop'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
        </div>
      ) : (
        <>
          {/* Hauptbutton */}
          <button
            onClick={handleHeuteErfassen}
            disabled={heuteErfasst || saving}
            className={`w-full flex items-center justify-center gap-3 py-4 rounded-xl text-sm font-bold transition-all ${
              heuteErfasst
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default'
                : 'text-white hover:opacity-90 active:scale-[0.98] shadow-md'
            }`}
            style={!heuteErfasst ? { backgroundColor: KOOP_RED } : undefined}
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : heuteErfasst ? (
              <>
                <Check className="w-5 h-5" />
                Heute bereits dokumentiert
              </>
            ) : (
              <>
                <UtensilsCrossed className="w-5 h-5" />
                Ich habe heute ein Mittagessen entnommen
              </>
            )}
          </button>

          {/* Monats-Info */}
          <div className="flex items-center justify-between text-xs text-gray-500 px-1">
            <span>{mahlzeitenMonat} Mahlzeit{mahlzeitenMonat !== 1 ? 'en' : ''} diesen Monat</span>
            <span className="font-medium">{(mahlzeitenMonat * SACHBEZUGSWERT_PRO_TAG).toFixed(2)} EUR Sachbezug</span>
          </div>

          {/* Hinweis */}
          <div className="flex items-start gap-2 bg-blue-50 rounded-lg px-3 py-2">
            <Info className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-[10px] text-blue-600 leading-relaxed">
              Obst, Kaffee und Wasser sind frei — keine Dokumentation noetig.
              Nur Tiefkuehlgerichte und Mittagessen muessen erfasst werden.
            </p>
          </div>

          {/* Nachtrag */}
          <button
            onClick={() => setShowNachtrag(!showNachtrag)}
            className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
          >
            {showNachtrag ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            Nachtrag fuer vergessene Tage
          </button>

          {showNachtrag && (
            <div className="space-y-1.5 pl-1">
              {letzteArbeitstage.map(tag => {
                const istErfasst = erfassteTage.has(tag);
                const label = new Date(tag).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' });
                return (
                  <button
                    key={tag}
                    onClick={() => !istErfasst && handleNachtrag(tag)}
                    disabled={istErfasst || saving}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs transition-colors ${
                      istErfasst
                        ? 'bg-gray-50 text-gray-400 cursor-default'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span>{label}</span>
                    {istErfasst ? (
                      <Check className="w-3.5 h-3.5 text-emerald-500" />
                    ) : (
                      <Plus className="w-3.5 h-3.5 text-gray-400" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
